export const SUPPORT_DEFAULT_REGION_PHONES = [
	{ region: "Africa", phone: "+234 8034567890" },
	{ region: "Asia", phone: "+91 9876543210" },
	{ region: "Canada", phone: "+1 4165552389" },
	{ region: "China", phone: "+86 1381234567" },
	{ region: "Europe", phone: "+44 7700900123" },
	{ region: "Latin America", phone: "+55 2199876543" },
	{ region: "Middle East", phone: "+971 501234567" },
	{ region: "America", phone: "+1 3056789123" },
];

export const SUPPORT_PHONE_PLACEHOLDER = "+countrycode 10digits";
export const SUPPORT_PHONE_REGEX = /^\+\d{1,4}\s\d{10}$/;

export const DEFAULT_SUPPORT_SETTINGS = {
	contactCcrShowEmergencyMessage: true,
	contactCcrEmergencyNumberSource: "region",
	contactCcrEmergencyNumberManual: "",
	createCaseShowEmergencyMessage: true,
	createCaseEmergencyNumberSource: "region",
	createCaseEmergencyNumberManual: "",
	enableErpCaseCreation: false,
	createCaseFieldVisibility: {
		requester_name: true,
		requester_email: true,
		requester_phone: true,
		machine_number: true,
	},
};

export const DEFAULT_CASE_TYPE_CONFIG = {
	name: "case-type",
	id: "case-type",
	default: {
		value: "",
		label: "Select Case Type",
		disabled: true,
		selected: true,
	},
	options: [
		{ value: "860580000", label: "Product Information" },
		{ value: "860580001", label: "Parts Related" },
		{ value: "860580002", label: "Tech Service Visit Request" },
		{ value: "860580003", label: "Tech Support" },
		{ value: "860580004", label: "General Inquiry" },
		{ value: "860580005", label: "AMF Method" },
		{ value: "860580006", label: "Repair and Modernization" },
	],
};

export const DEFAULT_CASE_SAFETY_CONFIG = {
	name: "case-safety",
	id: "case-safety",
	visible: false,
	default: {
		value: "",
		label: "Safety Issue/ Shutdown?",
		disabled: true,
	},
	options: [
		{ value: "860580000", label: "Yes" },
		{ value: "860580001", label: "No", selected: true },
	],
};

const parseBoolean = (value, fallback) => {
	if (value === "true" || value === true) return true;
	if (value === "false" || value === false) return false;
	return fallback;
};

const parseJson = (value, fallback) => {
	if (!value) return fallback;
	try {
		const parsed = typeof value === "string" ? JSON.parse(value) : value;
		return parsed ?? fallback;
	} catch {
		return fallback;
	}
};

export const isValidEmergencyPhone = (value = "") => SUPPORT_PHONE_REGEX.test(String(value || "").trim());

export const parseEmergencyPhones = (raw, fallback = SUPPORT_DEFAULT_REGION_PHONES) => {
	const parsed = parseJson(raw, null);
	if (!Array.isArray(parsed)) return fallback;
	const normalized = parsed
		.filter((row) => row && row.region)
		.map((row) => ({ region: row.region, phone: String(row.phone || "").trim() }));
	return normalized.length ? normalized : fallback;
};

const normalizeCaseOptionConfig = (raw, fallback) => {
	const parsed = parseJson(raw, fallback);
	const options = Array.isArray(parsed?.options)
		? parsed.options
			.map((item) => ({
				value: String(item?.value ?? "").trim(),
				label: String(item?.label ?? "").trim(),
				selected: item?.selected === true,
				disabled: item?.disabled === true,
			}))
			.filter((item) => item.value || item.label)
		: [];
	return {
		name: parsed?.name || fallback.name,
		id: parsed?.id || fallback.id,
		visible: parsed?.visible ?? fallback.visible,
		default: {
			value: String(parsed?.default?.value ?? fallback.default.value ?? "").trim(),
			label: String(parsed?.default?.label ?? fallback.default.label ?? "").trim(),
			disabled: parsed?.default?.disabled ?? fallback.default.disabled ?? false,
			selected: parsed?.default?.selected ?? fallback.default.selected ?? false,
		},
		options: options.length ? options : fallback.options,
	};
};

export const resolveSupportSettings = (optionKeys = {}) => ({
	contactCcrShowEmergencyMessage: parseBoolean(
		optionKeys.supportContactCcrShowEmergencyMessage,
		DEFAULT_SUPPORT_SETTINGS.contactCcrShowEmergencyMessage
	),
	contactCcrEmergencyNumberSource:
		optionKeys.supportContactCcrEmergencyNumberSource === "manual" ? "manual" : "region",
	contactCcrEmergencyNumberManual: String(optionKeys.supportContactCcrEmergencyNumberManual || "").trim(),
	createCaseShowEmergencyMessage: parseBoolean(
		optionKeys.supportCreateCaseShowEmergencyMessage,
		DEFAULT_SUPPORT_SETTINGS.createCaseShowEmergencyMessage
	),
	createCaseEmergencyNumberSource:
		optionKeys.supportCreateCaseEmergencyNumberSource === "manual" ? "manual" : "region",
	createCaseEmergencyNumberManual: String(optionKeys.supportCreateCaseEmergencyNumberManual || "").trim(),
	enableErpCaseCreation: parseBoolean(
		optionKeys.supportEnableErpCaseCreation,
		DEFAULT_SUPPORT_SETTINGS.enableErpCaseCreation
	),
	createCaseFieldVisibility: {
		...DEFAULT_SUPPORT_SETTINGS.createCaseFieldVisibility,
		...parseJson(optionKeys.supportCreateCaseFieldVisibility, {}),
	},
});

export const resolveCaseTypeConfig = (optionKeys = {}) =>
	normalizeCaseOptionConfig(optionKeys.supportCreateCaseTypeConfig, DEFAULT_CASE_TYPE_CONFIG);

export const resolveCaseSafetyConfig = (optionKeys = {}) =>
	normalizeCaseOptionConfig(optionKeys.supportCreateCaseSafetyConfig, DEFAULT_CASE_SAFETY_CONFIG);

export const resolveEmergencyNumberForRegion = ({
	source,
	manualNumber,
	userRegion,
	phones = SUPPORT_DEFAULT_REGION_PHONES,
}) => {
	if (source === "manual" && isValidEmergencyPhone(manualNumber)) {
		return manualNumber.trim();
	}
	const configured = Array.isArray(phones) ? phones : SUPPORT_DEFAULT_REGION_PHONES;
	if (!configured.length) return null;
	if (!userRegion) return configured[0]?.phone || null;
	return configured.find((row) => row.region === userRegion)?.phone || configured[0]?.phone || null;
};
